import React, { FC, PropsWithChildren, useEffect, useMemo } from 'react';
import {
  CssBaseline,
  Palette,
  ThemeProvider as TP,
  alpha,
  createTheme,
  darkScrollbar,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { Store } from '../redux';
import './font-family.css';
import './site-theme.css';
import { useLandscapeMode } from '@/shared/hooks/use-Landscape';
import { Capacitor } from '@capacitor/core';

// ── Sunday Table — the shared visual system ──────────────────────────────────
// The guest experience introduced a "Sunday card table" language: deep ink and
// forest felt, warm ivory paper, saffron + coral accents, Fraunces headings and
// Space Grotesk body. Account screens now speak the same language in both a
// dark ("night table") and a light ("day table", ivory paper) mode.
//
// The legacy `VOID` / `FROST` / `NORD` exports are kept — same keys — so any
// component still importing them compiles. Their values now map onto the
// Sunday Table palette (VOID = dark night table, FROST = light day table).

export const SUNDAY = {
  ink: '#0B1512',
  ink2: '#13201B',
  felt: '#123B2E',
  felt2: '#0E2E24',
  feltEdge: '#245A46',
  ivory: '#F7F0E1',
  ivory2: '#EBE1CB',
  paper: '#FFFAF0',
  saffron: '#F5A524',
  saffronLt: '#FFC35C',
  saffronDk: '#C77F00',
  saffronInk: '#8A4B00',
  coral: '#FF8466',
  coralInk: '#B4321C',
  green: '#8AD9A0',
  greenInk: '#1E7A45',
  redSuit: '#C22F37',
  onSaffron: '#1B1206',
} as const;

export const VOID = {
  // Backgrounds — deepest to elevated (night table)
  bg0: '#0B1512', // page canvas — ink
  bg1: '#11211B', // card / paper
  bg2: '#162B23', // elevated surface
  bg3: '#1D372D', // popover / tooltip

  // Borders & dividers
  line0: '#264035', // subtle separator
  line1: '#365646', // visible border

  // Primary — saffron (legacy name kept)
  cyan: SUNDAY.saffron,
  cyanLt: SUNDAY.saffronLt,
  cyanDk: SUNDAY.saffronDk,

  // Secondary — coral (legacy name kept)
  magenta: SUNDAY.coral,
  magentaLt: '#FFA58E',
  magentaDk: SUNDAY.coralInk,

  // Semantic accents
  green: SUNDAY.green,
  red: '#E5484D',
  amber: SUNDAY.saffron,
  blue: '#8CC7B0',

  // Text
  textPrimary: SUNDAY.ivory,
  textSecondary: '#BCCBBF',
  textDisabled: '#7E8F84',

  // Grays (mapped onto MUI grey scale). 900 is the black suit colour on
  // cards, so it stays ink.
  gray: {
    50: '#FBF7EE',
    100: SUNDAY.ivory,
    200: SUNDAY.ivory2,
    300: '#BCCBBF',
    400: '#7E8F84',
    500: '#3B5A4C',
    600: '#2A4237',
    700: '#1D372D',
    800: '#11211B',
    900: SUNDAY.ink,
    A100: SUNDAY.ivory,
    A200: SUNDAY.ivory2,
    A400: '#3B5A4C',
    A700: '#1D372D',
  },
};

// Light mode — "day table": ivory paper, ink text, saffron/coral accents.
export const FROST = {
  bg0: '#F4ECDB',
  bg1: SUNDAY.paper,
  bg2: '#F1E8D5',
  bg3: '#E8DCC2',
  line0: '#DCCDAE',
  line1: '#C2AE86',
  textPrimary: '#14231D',
  textSecondary: '#46554D',
  textDisabled: '#8A948C',
};

/* cspell:disable */
export const NORD = {
  nord0: VOID.bg0,
  nord1: VOID.bg1,
  nord2: VOID.bg2,
  nord3: VOID.line0,
  nord4: VOID.textDisabled,
  nord5: VOID.textSecondary,
  nord6: VOID.textPrimary,
  nord7: VOID.cyanLt,
  nord8: VOID.cyan,
  nord9: VOID.cyanDk,
  nord10: VOID.magentaDk,
  nord11: VOID.red,
  nord12: VOID.amber,
  nord13: VOID.green,
  nord14: VOID.green,
  nord15: VOID.magenta,
  gray: {
    darkGray50: VOID.gray[50],
    darkGray100: VOID.gray[100],
    darkGray200: VOID.gray[200],
    darkGray300: VOID.gray[300],
    darkGray400: VOID.gray[400],
    darkGray500: VOID.gray[500],
    darkGray600: VOID.gray[600],
    darkGray700: VOID.gray[700],
    darkGray800: VOID.gray[800],
    lightGray50: FROST.bg0,
    lightGray100: FROST.bg2,
    lightGray200: FROST.bg3,
    lightGray300: FROST.line0,
    lightGray400: FROST.line1,
    lightGray500: FROST.textDisabled,
    lightGray600: FROST.textSecondary,
    lightGray700: FROST.textPrimary,
    lightGray800: VOID.bg3,
  },
};
/* cspell:enable */

export const DISPLAY_FONT =
  '"Fraunces", "Iowan Old Style", Georgia, "Times New Roman", serif';
export const BODY_FONT =
  '"Space Grotesk", ui-sans-serif, system-ui, sans-serif';

// ── Palette slices ────────────────────────────────────────────────────────────

type PaletteSlice = Pick<
  Palette,
  | 'background'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'success'
  | 'info'
  | 'text'
  | 'divider'
  | 'action'
  | 'grey'
>;

export const darkModePalette: PaletteSlice = {
  background: { default: VOID.bg0, paper: VOID.bg1 },
  primary: {
    main: SUNDAY.saffron,
    light: SUNDAY.saffronLt,
    dark: SUNDAY.saffronDk,
    contrastText: SUNDAY.onSaffron,
  },
  secondary: {
    main: SUNDAY.coral,
    light: '#FFA58E',
    dark: SUNDAY.coralInk,
    contrastText: SUNDAY.ink,
  },
  error: {
    main: VOID.red,
    light: '#FF8A80',
    dark: '#B3261E',
    contrastText: '#fff',
  },
  warning: {
    main: SUNDAY.saffron,
    light: SUNDAY.saffronLt,
    dark: SUNDAY.saffronDk,
    contrastText: SUNDAY.onSaffron,
  },
  success: {
    main: SUNDAY.green,
    light: '#B4EBC2',
    dark: '#4FA86A',
    contrastText: SUNDAY.ink,
  },
  info: {
    main: VOID.blue,
    light: '#B7DECF',
    dark: '#4E8F78',
    contrastText: SUNDAY.ink,
  },
  text: {
    primary: VOID.textPrimary,
    secondary: VOID.textSecondary,
    disabled: VOID.textDisabled,
  },
  divider: alpha(SUNDAY.ivory, 0.14),
  grey: VOID.gray as Palette['grey'],
  action: {
    active: VOID.textPrimary,
    hover: SUNDAY.ivory,
    hoverOpacity: 0.08,
    selected: SUNDAY.saffron,
    selectedOpacity: 0.16,
    disabled: VOID.textDisabled,
    disabledBackground: VOID.bg3,
    disabledOpacity: 0.38,
    focus: SUNDAY.saffron,
    focusOpacity: 0.16,
    activatedOpacity: 0.16,
  },
};

export const lightModePalette: PaletteSlice = {
  background: { default: FROST.bg0, paper: FROST.bg1 },
  // Saffron itself is too light for text on ivory, so the light-mode primary is
  // the deeper "saffron ink". Filled primary buttons still use saffron.
  primary: {
    main: SUNDAY.saffronInk,
    light: SUNDAY.saffron,
    dark: '#6A3900',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: SUNDAY.coralInk,
    light: SUNDAY.coral,
    dark: '#8A2412',
    contrastText: '#FFFFFF',
  },
  error: {
    main: SUNDAY.redSuit,
    light: '#E5484D',
    dark: '#8F1D24',
    contrastText: '#fff',
  },
  warning: {
    main: '#9A5500',
    light: SUNDAY.saffron,
    dark: '#6A3900',
    contrastText: '#fff',
  },
  success: {
    main: SUNDAY.greenInk,
    light: '#4FA86A',
    dark: '#145531',
    contrastText: '#fff',
  },
  info: {
    main: SUNDAY.felt,
    light: SUNDAY.feltEdge,
    dark: SUNDAY.felt2,
    contrastText: '#fff',
  },
  text: {
    primary: FROST.textPrimary,
    secondary: FROST.textSecondary,
    disabled: FROST.textDisabled,
  },
  divider: alpha(FROST.textPrimary, 0.14),
  grey: {
    50: '#FBF7EE',
    100: FROST.bg2,
    200: FROST.bg3,
    300: FROST.line0,
    400: FROST.line1,
    500: FROST.textDisabled,
    600: FROST.textSecondary,
    700: FROST.textPrimary,
    800: VOID.bg2,
    900: SUNDAY.ink,
    A100: FROST.bg2,
    A200: FROST.bg3,
    A400: FROST.line1,
    A700: FROST.textSecondary,
  } as Palette['grey'],
  action: {
    active: FROST.textPrimary,
    hover: FROST.textPrimary,
    hoverOpacity: 0.06,
    selected: SUNDAY.saffronInk,
    selectedOpacity: 0.1,
    disabled: FROST.textDisabled,
    disabledBackground: FROST.bg3,
    disabledOpacity: 0.38,
    focus: SUNDAY.saffronInk,
    focusOpacity: 0.12,
    activatedOpacity: 0.14,
  },
};

const FOCUS_RING = `0 0 0 3px ${alpha(SUNDAY.saffron, 0.45)}`;

// ── ThemeProvider ─────────────────────────────────────────────────────────────

export const buildBhabiTheme = (
  mode: 'light' | 'dark',
  {
    isLandscapeMode = false,
    isNativePlatform = false,
  }: { isLandscapeMode?: boolean; isNativePlatform?: boolean } = {}
) => {
  const isDark = mode === 'dark';
  const slice = isDark ? darkModePalette : lightModePalette;
  const surface = isDark ? VOID : FROST;
  const textPrimary = isDark ? VOID.textPrimary : FROST.textPrimary;
  const line = slice.divider;

  return createTheme({
    palette: { ...slice, mode },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: isLandscapeMode ? 400 : 960,
        lg: 1280,
        xl: 1920,
      },
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily: BODY_FONT,
      fontSize: 13,
      h1: {
        fontFamily: DISPLAY_FONT,
        fontSize: '3rem',
        fontWeight: 800,
        lineHeight: 1,
        letterSpacing: '-0.025em',
        '@media only screen and (max-width: 600px)': { fontSize: '2.25rem' },
      },
      h2: {
        fontFamily: DISPLAY_FONT,
        fontSize: '2.25rem',
        fontWeight: 800,
        lineHeight: 1.05,
        letterSpacing: '-0.02em',
        '@media only screen and (max-width: 600px)': { fontSize: '1.75rem' },
      },
      h3: {
        fontFamily: DISPLAY_FONT,
        fontSize: '1.875rem',
        fontWeight: 700,
        lineHeight: 1.1,
        letterSpacing: '-0.015em',
        '@media only screen and (max-width: 600px)': { fontSize: '1.5rem' },
      },
      h4: {
        fontFamily: DISPLAY_FONT,
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: '-0.01em',
        '@media only screen and (max-width: 600px)': { fontSize: '1.3rem' },
      },
      h5: {
        fontFamily: BODY_FONT,
        fontSize: '1.2rem',
        fontWeight: 700,
        '@media only screen and (max-width: 600px)': { fontSize: '1.05rem' },
      },
      h6: {
        fontFamily: BODY_FONT,
        fontSize: '1rem',
        fontWeight: 700,
        '@media only screen and (max-width: 600px)': { fontSize: '0.95rem' },
      },
      button: { fontFamily: BODY_FONT, fontWeight: 700, textTransform: 'none' },
    },
    spacing: (factor: number) => `${factor * 6}px`,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // MUI injects this after index.css so it also reaches form controls.
          // Display-type elements opt back into Fraunces via site-theme.css.
          '*': { fontFamily: BODY_FONT },
          html: {
            ...darkScrollbar(
              isDark
                ? { track: VOID.bg1, thumb: VOID.line1, active: SUNDAY.saffron }
                : {
                    track: FROST.bg2,
                    thumb: FROST.line1,
                    active: SUNDAY.saffronInk,
                  }
            ),
            scrollbarWidth: 'thin',
            height: isNativePlatform ? '100%' : 'auto',
            overflow: isNativePlatform ? 'hidden' : 'auto',
          },
          body: {
            fontFamily: BODY_FONT,
            position: 'relative',
            minHeight: '100vh',
            height: isNativePlatform ? '100vh' : 'auto',
            overflow: isNativePlatform ? 'hidden' : 'auto',
            color: textPrimary,
            fontSize: 14,
            zIndex: 0,
            backgroundColor: surface.bg0,
            // Pure-CSS table surface; no image assets.
            backgroundImage: isDark
              ? `radial-gradient(120% 70% at 50% -10%, ${alpha(SUNDAY.saffron, 0.1)}, transparent 55%),
                 radial-gradient(80% 60% at 100% 100%, ${alpha(SUNDAY.coral, 0.06)}, transparent 60%),
                 linear-gradient(180deg, ${SUNDAY.felt} 0%, ${SUNDAY.felt2} 45%, ${SUNDAY.ink} 100%)`
              : `radial-gradient(120% 70% at 50% -10%, ${alpha(SUNDAY.saffron, 0.16)}, transparent 55%),
                 radial-gradient(80% 60% at 100% 100%, ${alpha(SUNDAY.coral, 0.08)}, transparent 60%),
                 linear-gradient(180deg, #F8F1E3 0%, ${FROST.bg0} 55%, #EFE4CC 100%)`,
            backgroundAttachment: 'fixed',
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: BODY_FONT,
            fontSize: 15,
            fontWeight: 700,
            textTransform: 'none',
            minHeight: 48,
            color: slice.text.secondary,
            '&.Mui-selected': { color: textPrimary },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
            backgroundColor: SUNDAY.saffron,
          },
        },
      },

      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': { boxShadow: FOCUS_RING },
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: BODY_FONT,
            minWidth: 64,
            minHeight: 44,
            padding: '8px 20px',
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: '0.01em',
            borderRadius: 999,
            textTransform: 'none',
            boxShadow: 'none',
            transition:
              'background-color .15s ease, box-shadow .15s ease, transform .15s ease',
            '&:active': { transform: 'translateY(1px)' },
          },
          sizeSmall: { minHeight: 36, padding: '4px 14px', fontSize: 13.5 },
          sizeLarge: { minHeight: 52, padding: '10px 26px', fontSize: 16.5 },
          containedPrimary: {
            // Saffron with ink text in both modes — matches guest `.st-btn--primary`.
            backgroundColor: SUNDAY.saffron,
            color: SUNDAY.onSaffron,
            boxShadow: `0 8px 20px ${alpha(SUNDAY.saffron, isDark ? 0.24 : 0.3)}, inset 0 -2px 0 rgba(0,0,0,.12)`,
            '&:hover': {
              backgroundColor: SUNDAY.saffronLt,
              boxShadow: `0 10px 24px ${alpha(SUNDAY.saffron, 0.32)}, inset 0 -2px 0 rgba(0,0,0,.12)`,
            },
            '&.Mui-disabled': {
              backgroundColor: isDark ? VOID.bg3 : FROST.bg3,
              color: slice.text.disabled,
              boxShadow: 'none',
            },
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5 },
          },
          outlinedPrimary: {
            borderColor: alpha(textPrimary, 0.4),
            color: textPrimary,
            '&:hover': {
              borderColor: textPrimary,
              backgroundColor: alpha(textPrimary, 0.06),
            },
          },
          textPrimary: {
            color: isDark ? SUNDAY.saffronLt : SUNDAY.saffronInk,
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 18,
            backgroundColor: surface.bg1,
            backgroundImage: 'none',
            border: `1px solid ${line}`,
            boxShadow: isDark
              ? '0 14px 34px rgba(0,0,0,.35)'
              : '0 10px 28px rgba(60,40,10,.10)',
          },
          elevation0: {
            background: 'transparent',
            boxShadow: 'none',
            border: 'none',
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 16 },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: { minHeight: 44 },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: BODY_FONT,
            fontSize: 12,
            fontWeight: 700,
            borderRadius: 999,
          },
        },
      },

      MuiAvatar: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `2px solid ${alpha(SUNDAY.saffron, isDark ? 0.55 : 0.7)}`,
            backgroundColor: isDark ? VOID.bg3 : FROST.bg3,
            color: textPrimary,
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            color: textPrimary,
            transition: 'background-color .15s ease',
            '&:hover': { backgroundColor: alpha(textPrimary, 0.08) },
          },
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              borderRadius: 14,
              backgroundColor: isDark ? alpha(SUNDAY.ivory, 0.06) : '#FFFDF7',
              transition: 'border-color .15s ease, box-shadow .15s ease',
            },
            '& .MuiInputBase-root:not(.MuiInputBase-multiline)': {
              minHeight: 44,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: alpha(textPrimary, isDark ? 0.22 : 0.24),
                borderWidth: 1.5,
              },
              '&:hover fieldset': {
                borderColor: alpha(textPrimary, 0.45),
              },
              '&.Mui-focused fieldset': {
                borderColor: SUNDAY.saffron,
                borderWidth: 2,
              },
              '&.Mui-focused': { boxShadow: FOCUS_RING },
              '&.Mui-error fieldset': { borderColor: slice.error.main },
            },
            '& .MuiInputBase-input': { color: textPrimary },
            '& .MuiInputBase-input::placeholder': {
              color: slice.text.secondary,
              opacity: 1,
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: isDark ? SUNDAY.saffronLt : SUNDAY.saffronInk,
            },
            '& .MuiFormHelperText-root': {
              fontSize: 13,
              marginLeft: 4,
              fontWeight: 600,
            },
          },
        },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: isDark ? SUNDAY.saffron : SUNDAY.saffronInk,
            },
          },
        },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: line } },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: SUNDAY.ink,
            color: SUNDAY.ivory,
            fontSize: '0.85rem',
          },
          arrow: { color: SUNDAY.ink },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            height: 6,
            backgroundColor: isDark ? VOID.bg3 : FROST.bg3,
          },
          bar: { borderRadius: 4, backgroundColor: SUNDAY.saffron },
        },
      },

      MuiCircularProgress: {
        styleOverrides: { circle: { strokeLinecap: 'round' } },
      },
    },
  });
};

export const ThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const { mode } = useSelector((state: Store) => state.themeReducer);

  const isLandscapeMode = useLandscapeMode();
  const isNativePlatform = Capacitor.isNativePlatform();

  const isDark = mode === 'dark';

  useEffect(() => {
    const htmlElement = document.documentElement;
    if (isDark) {
      htmlElement.classList.add('dark');
      htmlElement.classList.remove('light');
    } else {
      htmlElement.classList.add('light');
      htmlElement.classList.remove('dark');
    }
    localStorage.setItem('theme', mode);
  }, [mode, isDark]);

  const theme = useMemo(
    () => buildBhabiTheme(mode, { isLandscapeMode, isNativePlatform }),
    [mode, isLandscapeMode, isNativePlatform]
  );

  return (
    <TP theme={theme}>
      <CssBaseline enableColorScheme />
      {children}
    </TP>
  );
};
